import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Plus, ChevronDown, ChevronRight, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

import { Conversation } from '@shared/types';
import { AuthButton } from './auth/AuthButton';
import { CreditDisplay } from './CreditDisplay';
import { PricingModal } from './PricingModal';

interface SidebarProps {
  isSidebarOpen: boolean;
}

// Generate consistent color from string
function stringToColor(str: string): string {
  const colors = [
    'from-rose-500/30 to-pink-600/30',
    'from-violet-500/30 to-purple-600/30',
    'from-blue-500/30 to-cyan-600/30',
    'from-emerald-500/30 to-teal-600/30',
    'from-amber-500/30 to-orange-600/30',
    'from-fuchsia-500/30 to-pink-600/30',
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Sidebar({ isSidebarOpen }: SidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isFilesExpanded, setIsFilesExpanded] = useState(true);

  const { data: recentConversations } = useQuery<(Conversation & { thumbnailUrl?: string })[]>({
    queryKey: ['conversations', 'recent'],
    initialData: [],
    queryFn: async () => {
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('*')
        .order('updated_at', { ascending: false })
        .eq('user_id', user?.id ?? '')
        .limit(6);

      if (error) throw error;

      const conversationsWithDetails = await Promise.all(
        (conversations || []).map(async (conv) => {
          let title = conv.title;
          let thumbnailUrl: string | undefined;

          // Get title from first message if needed
          if (
            !title ||
            title.toLowerCase() === 'new conversation' ||
            title.toLowerCase() === 'untitled' ||
            title.toLowerCase() === 'conversation'
          ) {
            const { data: messages } = await supabase
              .from('messages')
              .select('content')
              .eq('conversation_id', conv.id)
              .eq('role', 'user')
              .order('created_at', { ascending: true })
              .limit(1);

            if (messages && messages.length > 0) {
              const firstMessage = messages[0];
              if (
                firstMessage.content &&
                typeof firstMessage.content === 'object' &&
                'text' in firstMessage.content
              ) {
                title = String(firstMessage.content.text || '').substring(0, 30).trim() || 'Untitled';
              }
            }
          }

          // Try to get a thumbnail from storage
          try {
            const { data: files } = await supabase.storage
              .from('images')
              .list(`${user?.id}/${conv.id}`, { limit: 5 });

            if (files && files.length > 0) {
              // Look for thumbnail.png first (captured from 3D view)
              const thumbnailFile = files.find(f => f.name === 'thumbnail.png');
              // Then look for render files (from STL uploads)
              const renderFile = files.find(f => f.name.includes('render-') && f.name.endsWith('.png'));
              // Then any other image
              const imageFile = thumbnailFile || renderFile || files.find(f =>
                f.name.endsWith('.png') || f.name.endsWith('.jpg') || f.name.endsWith('.jpeg') || f.name.endsWith('.webp')
              );

              if (imageFile) {
                const { data: signedUrl } = await supabase.storage
                  .from('images')
                  .createSignedUrl(`${user?.id}/${conv.id}/${imageFile.name}`, 3600); // 1 hour expiry

                thumbnailUrl = signedUrl?.signedUrl;
              }
            }
          } catch (_e) {
            // Ignore errors fetching thumbnails
            console.log('Could not fetch thumbnail for', conv.id);
          }

          return {
            ...conv,
            title: title || conv.title || 'Untitled',
            thumbnailUrl,
          };
        }),
      );

      return conversationsWithDetails;
    },

  });

  // Collapsed state - minimal icons only
  if (!isSidebarOpen) {
    return (
      <div className="w-14 relative flex h-full flex-shrink-0 flex-col bg-[#0e0e14] border-r border-white/5 transition-all duration-300 ease-out">
        {/* Logo */}
        <div className="p-2 flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#1a1a2e] to-[#12121a] border border-white/10">
                  <img
                    className="h-5 w-5 object-contain"
                    src={`${import.meta.env.BASE_URL}/daddycad-logo.png`}
                    alt="Logo"
                  />
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">DaddyCad</TooltipContent>
          </Tooltip>
        </div>

        {/* New Button */}
        <div className="p-2 flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 p-0 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all"
                onClick={() => navigate('/')}
              >
                <Plus className="h-5 w-5 text-white" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">New Creation</TooltipContent>
          </Tooltip>
        </div>

        {/* Files */}
        <div className="p-2 flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/history">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl hover:bg-white/5 transition-colors">
                  <svg className="h-5 w-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">Files</TooltipContent>
          </Tooltip>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Credits collapsed */}
        {user && !user.is_anonymous && (
          <div className="p-2 flex justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="p-0">
                <CreditDisplay />
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Auth */}
        <div className="p-2 border-t border-white/5">
          <AuthButton isSidebarOpen={false} />
        </div>

        <PricingModal open={showPricingModal} onOpenChange={setShowPricingModal} />
      </div>
    );
  }

  // Expanded state - full sidebar
  return (
    <div className="w-64 relative flex h-full flex-shrink-0 flex-col bg-[#0e0e14] border-r border-white/5 transition-all duration-300 ease-out">
      {/* Subtle gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-cyan-500/[0.02] to-transparent" />

      {/* Header - Logo */}
      <div className="relative z-10 px-4 py-4 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1a1a2e] to-[#12121a] border border-white/10 group-hover:border-white/20 transition-colors">
            <img
              className="h-5 w-5 object-contain"
              src={`${import.meta.env.BASE_URL}/daddycad-logo.png`}
              alt="Logo"
            />
          </div>
          <span className="text-[15px] font-semibold text-white">DaddyCad</span>
        </Link>
      </div>

      {/* Hero New Creation Button */}
      <div className="relative z-10 px-3 pb-4">
        <Button
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 group"
          onClick={() => navigate('/')}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20">
              <Plus className="h-5 w-5 text-white" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">New Creation</div>
              <div className="text-[10px] text-white/70">Design something new</div>
            </div>
          </div>
        </Button>
      </div>

      {/* Divider */}
      <div className="mx-4 h-px bg-white/5" />

      {/* Recent Files Section */}
      <div className="relative z-10 flex-1 overflow-hidden flex flex-col">
        {/* Section Header */}
        <button
          onClick={() => setIsFilesExpanded(!isFilesExpanded)}
          className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
        >
          <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Recent Files</span>
          {isFilesExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-zinc-600" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
          )}
        </button>

        {/* Files Grid */}
        {isFilesExpanded && (
          <div className="flex-1 overflow-y-auto px-3 pb-3">
            {recentConversations && recentConversations.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {recentConversations.slice(0, 6).map(
                    (conversation) => (
                      <Link to={`/editor/${conversation.id}`} key={conversation.id}>
                        <div className="group rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/10 transition-all duration-200 overflow-hidden">
                          {/* Thumbnail - real image or colored fallback */}
                          <div className={`h-16 relative flex items-center justify-center ${!conversation.thumbnailUrl ? `bg-gradient-to-br ${stringToColor(conversation.id)}` : 'bg-[#0a0a10]'}`}>
                            {conversation.thumbnailUrl ? (
                              <img
                                src={conversation.thumbnailUrl}
                                alt={conversation.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg className="h-6 w-6 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            )}
                          </div>
                          {/* File Name */}
                          <div className="p-2">
                            <p className="text-[11px] font-medium text-zinc-400 group-hover:text-zinc-200 line-clamp-1 transition-colors">
                              {conversation.title}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ),
                  )}
                </div>

                {/* View All Link */}
                <Link to="/history" className="mt-3 flex items-center justify-center gap-1.5 py-2 text-[11px] text-zinc-500 hover:text-cyan-400 transition-colors">
                  <span>View All Files</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="h-12 w-12 rounded-xl bg-white/5 flex items-center justify-center mb-3">
                  <svg className="h-6 w-6 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <p className="text-xs text-zinc-500">No files yet</p>
                <p className="text-[10px] text-zinc-600 mt-1">Create your first design!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 border-t border-white/5">
        {/* Credits */}
        {user && !user.is_anonymous && (
          <div className="px-3 py-2">
            <CreditDisplay onUpgradeClick={() => setShowPricingModal(true)} />
          </div>
        )}

        {/* Auth */}
        <div className="px-3 py-2 border-t border-white/5">
          <AuthButton isSidebarOpen={true} />
        </div>
      </div>

      <PricingModal open={showPricingModal} onOpenChange={setShowPricingModal} />
    </div>
  );
}
